import React from 'react';
import { CardBox } from 'components';

export const PrivacyPolicyFragment = () => {
    return (
        <div className="privacy-policy-fragment open-sans">
            <CardBox cardBackgroundColor={'#FFFFFF'}>
                <div className="privacy-policy-container">
                    <h3 className="content-title">Privacy Policy</h3>
                    <div className="privacy-policy-content">
                        <div className="container">
                            <p>
                                <strong>Effective Date:</strong> 07/24/2025
                            </p>
                            <p>
                                THERAPYLAKE LLC. (“TherapyLake,” “we,” “our,” or “us”) is committed to protecting your privacy. This Privacy
                                Policy describes how we collect, use, disclose, and safeguard personal information when you:
                            </p>
                            <ul>
                                <li>Visit our website at www.therapylake.com (the “Website”);</li>
                                <li>Use our software, digital tools, or services (collectively, “Services”); and</li>
                                <li>
                                    Communicate or engage with us in other ways, such as via email, support requests, or participation in
                                    webinars or events.
                                </li>
                            </ul>
                            <p>
                                By accessing or using any part of our Services, you accept the practices outlined in this Privacy Policy. If
                                you do not agree to this Policy, please do not use our Services. We may update this Privacy Policy
                                periodically. Continued use of our Services following such changes constitutes acceptance of those changes,
                                so we encourage you to review this Policy from time to time.
                            </p>

                            <h2>1. Scope of This Privacy Policy</h2>
                            <p>
                                This Privacy Policy applies when TherapyLake acts as a data controller, meaning we determine the purposes
                                and means of processing your personal information. This includes personal data collected from website
                                visitors, prospective clients, event participants, and account users.
                            </p>
                            <p>
                                In cases where we provide services to healthcare providers or other organizations (our “Clients”), we may
                                process data on their behalf. In those situations, TherapyLake is acting as a data processor or service
                                provider, depending on applicable privacy laws. Our Clients are responsible for how user data is used under
                                their own privacy practices. If you use our platform through one of our Clients and have questions about how
                                your personal data is handled, please contact that organization directly.
                            </p>

                            <h2>2. Information We Collect and How We Collect It</h2>
                            <p>
                                We collect only the personal information necessary to deliver, maintain, improve, and support our Services.
                                The nature and extent of information we collect depends on how you interact with us.
                            </p>

                            <h3>A. Information You Provide to Us</h3>
                            <p>You may provide personal information to us directly in various ways, including:</p>
                            <ul>
                                <li>When you register for or access the Services;</li>
                                <li>When you submit inquiries, requests, or feedback through forms, emails, or support channels;</li>
                                <li>When participating in product demos, webinars, or surveys;</li>
                                <li>When signing up for newsletters or marketing communications;</li>
                                <li>When interacting with our customer success or onboarding team;</li>
                                <li>
                                    When using interactive features on our Website or platform (such as search tools, user-generated
                                    content, or account preferences);
                                </li>
                                <li>When attending events or providing feedback.</li>
                            </ul>
                            <p>Types of information you may provide include:</p>
                            <ul>
                                <li>Contact details (e.g., name, email address, mailing address, phone number);</li>
                                <li>Account credentials (e.g., username and password);</li>
                                <li>Company or employment-related data (e.g., organization name, role/title);</li>
                                <li>Billing details (e.g., payment card info, billing address);</li>
                                <li>Communication preferences and other settings;</li>
                                <li>Usage data and technical support history;</li>
                                <li>
                                    <strong>Any additional information you voluntarily submit during your use of the Services.</strong>
                                </li>
                            </ul>
                            <p>
                                You may also post or transmit content (“User Contributions”) in areas of the Services that are visible to
                                others. Please be aware that any information you share in public or shared areas may be visible to other
                                users, and we cannot control how they use or disclose that information.
                            </p>

                            <h3>B. Information We Process on Behalf of Clients</h3>
                            <p>
                                In many cases, we provide our software and platform services to organizations (“Clients”) who, in turn,
                                authorize their own users to access the Services. When acting on behalf of a Client, we process personal
                                information solely as a “service provider” or “data processor” under applicable privacy laws.
                            </p>
                            <p>
                                This means that we handle personal information only as instructed by the Client and in accordance with our
                                contract or agreement with them. Examples include:
                            </p>
                            <ul>
                                <li>User profile information created by a Client account;</li>
                                <li>Data input or uploaded by users of a Client’s organization;</li>
                                <li>Logs or reports generated through use of the platform.</li>
                            </ul>
                            <p>
                                Each Client is responsible for its own privacy practices and compliance. If you use our Services as part of
                                an organization’s account, and have questions about your personal data or wish to exercise any rights, you
                                should contact that organization directly.
                            </p>
                            <p>
                                We do not use personal data received in this context for our own purposes, except as necessary to provide
                                and maintain the Services, comply with legal obligations, or improve security and performance.
                            </p>

                            <h3>C. Information We Collect Through Automated Means</h3>
                            <p>
                                When you interact with our website or platform, we may automatically gather certain technical and usage
                                information using standard digital tracking technologies. This helps us understand how our Services are
                                being used and how to improve them.
                            </p>
                            <p>Examples of data we may collect automatically include:</p>
                            <ul>
                                <li>Your device type, browser, and operating system</li>
                                <li>IP address and general location</li>
                                <li>Pages viewed and links clicked</li>
                                <li>Time spent on specific parts of the platform</li>
                                <li>Files accessed or downloaded</li>
                                <li>Referring and exit pages</li>
                                <li>Date and time of your visits</li>
                            </ul>
                            <p>We collect this data using tools such as:</p>
                            <ul>
                                <li>Cookies – small text files stored on your device to help remember your preferences and settings</li>
                                <li>Web beacons / pixels – small image files embedded in web pages or emails that help track engagement</li>
                                <li>
                                    Local storage (e.g., HTML5) – used to retain information locally in your browser for improved
                                    performance
                                </li>
                            </ul>
                            <p>
                                We may also use third-party analytics services (such as Google Analytics) to help us analyze trends and
                                usage patterns. These services may use their own cookies or similar technologies to collect information
                                about your use of the Services. For information on how Google Analytics collects and processes data, and how
                                to opt out, visit:{' '}
                                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                                    https://tools.google.com/dlpage/gaoptout
                                </a>
                                .
                            </p>
                            <p>
                                If you prefer, you may configure your browser to refuse some or all cookies. Please note, however, that
                                disabling cookies may limit the functionality of certain features.
                            </p>
                            <p>Our website does not respond to “Do Not Track” browser signals at this time.</p>

                            <h3>D. Information Collected from Other Sources</h3>
                            <p>
                                In some cases, we may collect information about you from third-party sources, where permitted by applicable
                                law. These sources may include:
                            </p>
                            <ul>
                                <li>Publicly available databases and websites</li>
                                <li>Professional or social networks (such as LinkedIn or similar platforms)</li>
                                <li>Business contact directories or lead generation tools</li>
                                <li>Event co-sponsors or partners</li>
                                <li>Referrals from other customers, colleagues, or business contacts</li>
                            </ul>
                            <p>
                                We use this information to supplement our existing data, better understand your interests, tailor
                                communications, and enhance the relevance of our Services. Any information we collect from third parties
                                will be processed in accordance with this Privacy Policy and applicable laws.
                            </p>

                            <h2>3. How We Use the Information We Obtain</h2>
                            <p>
                                We use the information we collect about you for a variety of purposes, depending on how you interact with
                                our Services. These purposes include:
                            </p>
                            <ul>
                                <li>
                                    Providing and operating our Services, including account setup, access permissions, subscription
                                    management, and technical support.
                                </li>
                                <li>
                                    Improving and enhancing our Services, such as analyzing performance, identifying bugs, optimizing
                                    usability, and developing new features.
                                </li>
                                <li>
                                    Responding to your communications, including questions, support inquiries, demo requests, or feedback.
                                </li>
                                <li>
                                    Sending you updates and communications, such as newsletters, announcements, service-related notices, or
                                    marketing messages (where permitted).
                                </li>
                                <li>
                                    Personalizing your experience, including tailoring features, recommendations, and content based on your
                                    interactions or preferences.
                                </li>
                                <li>
                                    Conducting analytics and research, including usage trends, customer feedback analysis, and product
                                    improvement efforts.
                                </li>
                                <li>
                                    Managing legal and compliance obligations, including fraud detection, dispute resolution, and
                                    enforcement of our Terms of Service or other policies.
                                </li>
                                <li>
                                    Fulfilling contractual obligations, such as providing services under a customer agreement or responding
                                    to third-party requests where legally required.
                                </li>
                                <li>
                                    Any other use for which we obtain your consent, or as otherwise permitted or required by applicable law.
                                </li>
                            </ul>
                            <p>
                                We may also convert personal information into aggregated or de-identified data that cannot reasonably be
                                used to identify you, and use or disclose such information for any purpose.
                            </p>

                            <h2>4. Information We Share</h2>
                            <p>
                                We do not sell your personal information. However, in the course of operating our business and delivering
                                our Services, we may share personal information as follows:
                            </p>
                            <ul>
                                <li>
                                    With affiliated entities within the TherapyLake family, for purposes consistent with this Privacy
                                    Policy.
                                </li>
                                <li>
                                    With trusted service providers who help us perform essential business functions, such as payment
                                    processors, hosting services, analytics providers, communication platforms, and customer support tools.
                                </li>
                                <li>
                                    With your organization, if you access our Services under a customer account (e.g., your employer or
                                    service provider), to support administration, compliance, and usage insights.
                                </li>
                                <li>
                                    As directed by you, including when you explicitly request or consent to us sharing your information with
                                    others.
                                </li>
                                <li>
                                    To comply with legal requirements, including in response to subpoenas, court orders, government
                                    requests, or applicable laws and regulations.
                                </li>
                                <li>
                                    To protect our rights and users, such as to investigate suspected fraud, enforce our agreements, or
                                    prevent harm.
                                </li>
                                <li>
                                    In connection with a business transition, such as a merger, acquisition, reorganization, sale of assets,
                                    or bankruptcy. In such cases, your personal information may be transferred as part of that transaction.
                                </li>
                            </ul>
                            <p>
                                In all cases, we take appropriate steps to ensure that any third parties receiving your data are
                                contractually obligated to protect it and use it only for the intended purposes.
                            </p>

                            <h2>5. Retention of Your Information</h2>
                            <p>
                                We retain personal information only for as long as reasonably necessary to fulfill the purposes for which it
                                was collected, including to meet legal, regulatory, accounting, or reporting requirements.
                            </p>
                            <p>The specific duration depends on several factors, including:</p>
                            <ul>
                                <li>The nature of the information collected;</li>
                                <li>The purpose for its collection and use;</li>
                                <li>Legal obligations under applicable laws (such as data retention requirements);</li>
                                <li>Contractual obligations with our clients and partners;</li>
                                <li>Ongoing customer support needs or dispute resolution.</li>
                            </ul>
                            <p>
                                Once the retention period ends, we securely delete, anonymize, or de-identify the data in accordance with
                                our data destruction policies.
                            </p>
                            <p>
                                If you have an account with us and terminate your services, we may retain limited information necessary for
                                compliance, fraud prevention, or audit purposes, unless deletion is required sooner by law or requested and
                                permitted under applicable data protection rules.
                            </p>

                            <h2>6. Your Choices and Rights</h2>
                            <p>
                                You have a number of choices regarding how we collect, use, and share your personal information. We strive
                                to provide transparent options and control over your data.
                            </p>

                            <h3>A. Cookies and Tracking Technologies</h3>
                            <p>
                                You can set your browser to refuse some or all browser cookies, or to alert you when cookies are being sent.
                                If you disable or reject cookies, some features of our Website or Services may not function as intended.
                            </p>

                            <h3>B. Marketing Communications</h3>
                            <p>
                                If you receive promotional emails from TherapyLake, you can opt out at any time by clicking the
                                "unsubscribe" link at the bottom of the message.
                            </p>
                            <p>
                                <strong>Note:</strong> Even if you opt out of marketing emails, you may still receive service-related
                                communications, such as security alerts or notices related to your account or our services.
                            </p>

                            <h3>C. Account Information</h3>
                            <p>
                                If you have an account with us, you can access and update certain personal information by logging into your
                                account settings. You may also request updates or deletions by contacting us directly.
                            </p>
                            <p>
                                <strong>Please note:</strong> Removing certain data may impact your ability to use some or all of our
                                services.
                            </p>

                            <h3>D. User-Generated Content</h3>
                            <p>
                                If you post content or submit information in public areas of our Services, such contributions may remain
                                visible even after deletion in cached or archived pages, or if other users have copied or stored them. We
                                cannot guarantee complete removal of this content.
                            </p>

                            <h3>E. Privacy Rights by Jurisdiction</h3>
                            <p>
                                Depending on your location, you may have additional rights under local data privacy laws. These may include
                                the right to access, correct, delete, or restrict the use of your information. If you wish to exercise such
                                rights, please contact us at: <a href="mailto:info@therapylake.com">info@therapylake.com</a>.
                            </p>

                            <h2>7. Security</h2>
                            <p>
                                We implement a range of administrative, technical, and physical safeguards designed to protect the personal
                                information we collect and store. These controls aim to prevent accidental, unlawful, or unauthorized
                                destruction, loss, access, disclosure, alteration, or use.
                            </p>
                            <p>
                                That said, no system or method of internet transmission is completely secure. While we take reasonable steps
                                to secure your data, we cannot guarantee the absolute security of information transmitted to or from our
                                Website or Services. Transmission of personal information is done at your own risk.
                            </p>
                            <p>
                                If you become aware of or suspect any unauthorized access to your TherapyLake account or data, please notify
                                us immediately by contacting us at <a href="mailto:info@therapylake.com">info@therapylake.com</a>.
                            </p>

                            <h2>8. Children’s Privacy</h2>
                            <p>
                                Our Services are designed for adult professionals and are not intended for use by individuals under the age
                                of 18. We do not knowingly collect or solicit personal information from anyone under 18. If we become aware
                                that we have collected personal information from a child under 18 without verified parental consent, we will
                                take steps to promptly delete that information.
                            </p>
                            <p>
                                If you believe we may have collected information from or about a child under 18, please contact us
                                immediately at <a href="mailto:info@therapylake.com">info@therapylake.com</a>.
                            </p>

                            <h2>9. Other Online Services and Third-Party Features</h2>
                            <p>
                                For your convenience, our Website and Services may include links to other websites or may incorporate
                                third-party features such as tools, widgets, and plugins (e.g., video conferencing apps, social media
                                sharing tools, or analytics tools). If you choose to interact with these third-party services, please note
                                that your information will be subject to the privacy policies of those third parties—not this Privacy
                                Policy.
                            </p>
                            <p>
                                We are not responsible for the content, privacy practices, or data handling of any third-party sites or
                                services that are not under our control. We encourage you to review the privacy notices of those third
                                parties before engaging with their features or submitting personal information through them.
                            </p>

                            <h2>10. Changes to This Privacy Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal
                                requirements, or for other operational reasons. When we make changes, we will revise the “Effective Date” at
                                the top of this page.
                            </p>
                            <p>
                                If we make any material changes that affect how we handle your personal information, we will provide
                                prominent notice—such as by posting a notice on our website or by sending an email—where required by
                                applicable law.
                            </p>
                            <p>
                                We encourage you to review this Privacy Policy periodically to stay informed about how we collect, use, and
                                share your information.
                            </p>

                            <h2>Contact Us</h2>
                            <p>
                                If you have questions or concerns regarding this Privacy Policy or the collection and use of your personal
                                information, please contact us:
                            </p>
                            <ul>
                                <li>
                                    By email: <a href="mailto:info@therapylake.com">info@therapylake.com</a>
                                </li>
                                <li>By phone: (424) 277-0772</li>
                                <li>
                                    By mail:
                                    <br />
                                    TherapyLake
                                    <br />
                                    4100 W. Alameda Ave.
                                    <br />
                                    Burbank, CA 91505
                                </li>
                            </ul>
                            {/*{privacyPolicyParagraphs.map((paragraph, index) => (*/}
                            {/*    <p key={index} className="privacy-policy-paragraph">*/}
                            {/*        {paragraph}*/}
                            {/*    </p>*/}
                            {/*))}*/}
                        </div>
                    </div>
                </div>
            </CardBox>
        </div>
    );
};
